/* Keeps the cosmetic promo price on screen after a variant change.
 *
 * Why this exists: global.js `updatePriceForVariant()` replaces the whole
 * innerHTML of `.price__container` when a shopper picks a size or colour, which
 * throws away the struck-through price Liquid rendered. Rather than fork that
 * vendor function, watch the container and re-apply.
 *
 * Display only. Nothing here touches the cart, the line item, or what is
 * charged: the real money comes off via the automatic discount at checkout.
 */
(function () {
  "use strict";

  var SELECTOR = ".price--promo[data-promo-percent]";

  // "$1,234.56" -> 1234.56 ; "CA$350.00 CAD" -> 350
  // Returns null rather than guessing when the string has no clean money token,
  // so an unfamiliar currency format leaves the price untouched instead of
  // rendering a wrong number.
  function parseMoney(text) {
    var match = String(text).match(/\d[\d.,\s ]*/);
    if (!match) return null;
    var raw = match[0].replace(/[\s ]/g, "");
    var lastComma = raw.lastIndexOf(",");
    var lastDot = raw.lastIndexOf(".");
    var decimal = Math.max(lastComma, lastDot);
    var value;
    // A separator with exactly two digits after it is the decimal point;
    // anything else (1,234 / 1.234) is a thousands separator.
    if (decimal > -1 && raw.length - decimal - 1 === 2) {
      value = parseFloat(
        raw.slice(0, decimal).replace(/[.,]/g, "") + "." + raw.slice(decimal + 1)
      );
    } else {
      value = parseFloat(raw.replace(/[.,]/g, ""));
    }
    return isNaN(value) ? null : value;
  }

  // Rebuild the money string in the same shape the theme printed it, so the
  // currency symbol, code and separators all survive.
  function formatLike(template, value) {
    var match = String(template).match(/\d[\d.,\s ]*/);
    if (!match) return null;
    var hasDecimals = /[.,]\d{2}$/.test(match[0].replace(/[\s ]/g, ""));
    var groupSep = match[0].indexOf(",") > -1 && match[0].indexOf(".") > -1
      ? match[0].indexOf(",") < match[0].indexOf(".") ? "," : "."
      : ",";
    var decSep = groupSep === "," ? "." : ",";
    var fixed = hasDecimals ? value.toFixed(2) : String(Math.round(value));
    var parts = fixed.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    return template.replace(match[0], parts.join(decSep));
  }

  function apply(priceEl) {
    var container = priceEl.querySelector(".price__container");
    if (!container) return;
    // Already rendered: Liquid did it, or we did it on a previous pass.
    if (container.querySelector(".promo-price")) return;

    var percent = parseFloat(priceEl.getAttribute("data-promo-percent"));
    if (!percent || percent <= 0 || percent >= 100) return;

    // Take the first money token the theme just wrote as the full price.
    var source = container.querySelector("ins, s, .price__regular, .price__sale-inner")
      || container;
    var text = source.textContent.trim();
    var full = parseMoney(text);
    if (full === null) return;

    var was = formatLike(text, full);
    var now = formatLike(text, full * (100 - percent) / 100);
    if (was === null || now === null) return;

    var wrap = document.createElement("span");
    wrap.className = "promo-price";
    // Sale price first, then the struck full price, matching the Liquid.
    wrap.innerHTML =
      '<ins class="promo-price__now"></ins>' +
      '<s class="promo-price__was"></s>';
    wrap.querySelector(".promo-price__now").textContent = now;
    wrap.querySelector(".promo-price__was").textContent = was;

    container.replaceChildren(wrap);
  }

  function applyAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(apply);
  }

  function watch() {
    applyAll();
    // global.js swaps innerHTML on variant change, and sections can be
    // re-rendered by the section rendering API, so watch the whole document
    // rather than binding to one form.
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        applyAll();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", watch);
  } else {
    watch();
  }
})();
